/**
 * Synthesised sound effects for the prize carousel.
 *
 * Everything is generated with the Web Audio API rather than shipped as audio
 * files: no extra network requests on a kiosk that may be offline, and the
 * timings can be tuned to match the card choreography exactly.
 *
 * Browsers block audio until a user gesture, so `unlockCarouselAudio()` must be
 * called from a click handler (the spin button) before anything will be heard.
 */

const STORAGE_KEY = 'carousel:sound-enabled';

type Ctx = AudioContext;

let ctx: Ctx | null = null;
let master: GainNode | null = null;
let noiseBuffer: AudioBuffer | null = null;

let enabled = readStoredPreference();
const listeners = new Set<() => void>();

function readStoredPreference(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

function ensureContext(): Ctx | null {
  if (typeof window === 'undefined') return null;

  const Ctor =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;

  if (!ctx) {
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.8;

    // The build-up deliberately stacks many voices at once (riffle + ticks +
    // thump + riser). Without a limiter those sum past 0dB and the TV speakers
    // produce crackle rather than punch.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -10;
    limiter.knee.value = 6;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.2;

    master.connect(limiter).connect(ctx.destination);
  }

  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Live context only when sound is on — keeps every play* call a cheap no-op. */
function audio(): { ctx: Ctx; out: GainNode } | null {
  if (!enabled) return null;
  const c = ensureContext();
  if (!c || !master) return null;
  return { ctx: c, out: master };
}

function getNoiseBuffer(c: Ctx): AudioBuffer {
  if (!noiseBuffer) {
    const length = Math.floor(c.sampleRate * 0.5);
    noiseBuffer = c.createBuffer(1, length, c.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  }
  return noiseBuffer;
}

interface NoiseOptions {
  at?: number;
  duration?: number;
  gain?: number;
  freqFrom?: number;
  freqTo?: number;
  q?: number;
}

/** Filtered noise burst — the basis of card snaps and whooshes. */
function noise({ at = 0, duration = 0.12, gain = 0.2, freqFrom = 1200, freqTo = 1200, q = 1 }: NoiseOptions) {
  const a = audio();
  if (!a) return;
  const { ctx: c, out } = a;
  const t = c.currentTime + at;

  const src = c.createBufferSource();
  src.buffer = getNoiseBuffer(c);

  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.Q.value = q;
  filter.frequency.setValueAtTime(freqFrom, t);
  filter.frequency.exponentialRampToValueAtTime(Math.max(40, freqTo), t + duration);

  const env = c.createGain();
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(gain, t + duration * 0.16);
  env.gain.exponentialRampToValueAtTime(0.0001, t + duration);

  src.connect(filter).connect(env).connect(out);
  src.start(t);
  src.stop(t + duration + 0.02);
}

interface ToneOptions {
  freq: number;
  at?: number;
  duration?: number;
  gain?: number;
  type?: OscillatorType;
  glideTo?: number;
}

function tone({ freq, at = 0, duration = 0.3, gain = 0.16, type = 'triangle', glideTo }: ToneOptions) {
  const a = audio();
  if (!a) return;
  const { ctx: c, out } = a;
  const t = c.currentTime + at;

  const osc = c.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, t + duration);

  const env = c.createGain();
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(gain, t + 0.015);
  env.gain.exponentialRampToValueAtTime(0.0001, t + duration);

  osc.connect(env).connect(out);
  osc.start(t);
  osc.stop(t + duration + 0.02);
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

/** Call from a click handler before the first sound of a session. */
export function unlockCarouselAudio() {
  if (!enabled) return;
  ensureContext();
}

export function isCarouselSoundEnabled() {
  return enabled;
}

export function setCarouselSoundEnabled(value: boolean) {
  enabled = value;
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? 'on' : 'off');
  } catch {
    /* private mode — preference just won't persist */
  }
  if (!value && ctx) void ctx.suspend();
  if (value) ensureContext();
  listeners.forEach((l) => l());
}

export function subscribeCarouselSound(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A single card snapping against the others. */
export function playCardSnap(at = 0, gain = 0.09) {
  noise({ at, duration: 0.05, gain, freqFrom: 2600, freqTo: 1400, q: 1.6 });
}

/** Bright mechanical click — the reel-tick that drives the tension. */
export function playTick(at = 0, gain = 0.07) {
  noise({ at, duration: 0.03, gain, freqFrom: 4200, freqTo: 2600, q: 3 });
  tone({ freq: 2400, at, duration: 0.035, gain: gain * 0.5, type: 'square' });
}

/**
 * One riffle pass — a cascade of card snaps.
 * `intensity` (0–3) tightens and speeds up the cascade as the spin builds.
 */
export function playRiffle(intensity = 0) {
  const snaps = 8 + intensity * 3;
  const spread = 0.44 - intensity * 0.09;
  for (let i = 0; i < snaps; i++) {
    // Accelerating cascade, like a real riffle releasing.
    const at = 0.02 + Math.pow(i / snaps, 1.5) * spread;
    playCardSnap(at, 0.05 + (i / snaps) * 0.05 + intensity * 0.012);
  }
}

/** Low heartbeat thump, used to underpin the later stages of the spin. */
export function playThump(at = 0, gain = 0.22) {
  const a = audio();
  if (!a) return;
  const { ctx: c, out } = a;
  const t = c.currentTime + at;

  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(150, t);
  osc.frequency.exponentialRampToValueAtTime(45, t + 0.18);

  const env = c.createGain();
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(gain, t + 0.012);
  env.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);

  osc.connect(env).connect(out);
  osc.start(t);
  osc.stop(t + 0.32);
}

/** Impact for the winning card landing. */
export function playImpact() {
  playThump(0, 0.32);
  noise({ duration: 0.5, gain: 0.16, freqFrom: 3000, freqTo: 200, q: 0.5 });
}

/* --- Rising tension riser -------------------------------------------- */

let riser: { osc: OscillatorNode; sweep: AudioBufferSourceNode | null } | null = null;

/** Pitch sweep that climbs for `seconds` — the "something's coming" cue. */
export function startRiser(seconds: number) {
  const a = audio();
  if (!a) return;
  stopRiser();

  const { ctx: c, out } = a;
  const t = c.currentTime;

  const osc = c.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(140, t);
  osc.frequency.exponentialRampToValueAtTime(1500, t + seconds);

  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 6;
  filter.frequency.setValueAtTime(600, t);
  filter.frequency.exponentialRampToValueAtTime(7000, t + seconds);

  const env = c.createGain();
  env.gain.setValueAtTime(0.0001, t);
  env.gain.exponentialRampToValueAtTime(0.1, t + seconds * 0.9);
  env.gain.exponentialRampToValueAtTime(0.0001, t + seconds);

  osc.connect(filter).connect(env).connect(out);
  osc.start(t);
  osc.stop(t + seconds + 0.05);

  // Airy noise sweep layered on top for body.
  const sweep = c.createBufferSource();
  sweep.buffer = getNoiseBuffer(c);
  sweep.loop = true;
  const sweepFilter = c.createBiquadFilter();
  sweepFilter.type = 'bandpass';
  sweepFilter.Q.value = 2;
  sweepFilter.frequency.setValueAtTime(500, t);
  sweepFilter.frequency.exponentialRampToValueAtTime(6000, t + seconds);
  const sweepEnv = c.createGain();
  sweepEnv.gain.setValueAtTime(0.0001, t);
  sweepEnv.gain.exponentialRampToValueAtTime(0.05, t + seconds * 0.9);
  sweepEnv.gain.exponentialRampToValueAtTime(0.0001, t + seconds);
  sweep.connect(sweepFilter).connect(sweepEnv).connect(out);
  sweep.start(t);
  sweep.stop(t + seconds + 0.05);

  riser = { osc, sweep };
}

export function stopRiser() {
  if (!riser) return;
  try {
    riser.osc.stop();
    riser.sweep?.stop();
  } catch {
    /* already stopped */
  }
  riser = null;
}

/** A losing card turning over. */
export function playCardFlip(at = 0) {
  noise({ at, duration: 0.2, gain: 0.11, freqFrom: 500, freqTo: 2600, q: 0.8 });
  playCardSnap(at + 0.17, 0.07);
}

/** The winning card turning over — heavier, with a rising sting and an impact. */
export function playWinnerFlip() {
  stopRiser();
  noise({ duration: 0.34, gain: 0.2, freqFrom: 320, freqTo: 3200, q: 0.7 });
  tone({ freq: 330, glideTo: 880, duration: 0.34, gain: 0.1, type: 'sawtooth' });
  playCardSnap(0.3, 0.16);
  playImpact();
  tone({ freq: 1318.51, at: 0.32, duration: 0.6, gain: 0.12, type: 'sine' });
  tone({ freq: 1975.53, at: 0.32, duration: 0.5, gain: 0.06, type: 'sine' });
}

/** Victory fanfare. `big` is the jackpot variant. */
export function playFanfare(big: boolean) {
  const a = audio();
  if (!a) return;

  stopRiser();
  playThump(0, 0.26);

  if (!big) {
    // C5 – E5 – G5 – C6 with an octave shimmer on top
    [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      tone({ freq, at: i * 0.1, duration: 0.55, gain: 0.15, type: 'triangle' });
      tone({ freq: freq * 2, at: i * 0.1, duration: 0.35, gain: 0.04, type: 'sine' });
    });
    tone({ freq: 261.63, at: 0, duration: 1.2, gain: 0.07, type: 'sine' }); // bass root
    tone({ freq: 2093, at: 0.42, duration: 0.8, gain: 0.06, type: 'sine' });
    noise({ at: 0.4, duration: 0.7, gain: 0.04, freqFrom: 7000, freqTo: 3000, q: 0.8 });
    return;
  }

  // Jackpot: rising fanfare over a bass root, a sustained chord, then sparkle.
  tone({ freq: 130.81, at: 0, duration: 2.2, gain: 0.09, type: 'sine' });
  [392, 523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
    tone({ freq, at: i * 0.095, duration: 0.6, gain: 0.17, type: 'triangle' });
    tone({ freq: freq * 2, at: i * 0.095, duration: 0.4, gain: 0.05, type: 'sine' });
  });

  // Triumphant held chord under the sparkle
  [523.25, 659.25, 783.99, 1046.5].forEach((freq) => {
    tone({ freq, at: 0.5, duration: 1.8, gain: 0.085, type: 'triangle' });
  });
  playThump(0.5, 0.24);

  // Cascading sparkle, denser at the start then thinning out
  for (let i = 0; i < 20; i++) {
    tone({
      freq: 1400 + Math.random() * 2200,
      at: 0.55 + i * 0.065,
      duration: 0.3,
      gain: 0.045,
      type: 'sine',
    });
  }

  noise({ at: 0.48, duration: 1.1, gain: 0.06, freqFrom: 7000, freqTo: 2000, q: 0.6 });
}
